/**
 * TimeOffApproval.jsx
 * Manager approval queue for all time-off requests
 * 
 * Features:
 * - Unified view of all leave types
 * - Statutory leave indicators (cannot refuse)
 * - Response deadline tracking
 * - Mandatory refusal reasons
 * - Auto-generate approval/refusal letters
 * - Audit trail integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from './axiosConfig';
import axios from 'axios';
import { DocumentList } from './DocumentUpload';
import { 
  Box, Typography, Paper, Button, Snackbar, Alert,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Chip,
  Card, CardContent, CardActions, Grid, Divider, Tooltip,
  IconButton, Collapse, Badge, FormControlLabel, Checkbox
} from '@mui/material';
import {
  CheckCircle, Cancel, AccessTime, Warning, Gavel,
  ExpandMore, ExpandLess, Person, CalendarToday,
  Description, Info, Download
} from '@mui/icons-material';

// Leave type configuration
const LEAVE_CONFIG = {
  standard_holiday: {
    name: 'Annual Leave',
    icon: '🌴',
    color: '#0D9488',
    canRefuse: true
  },
  annual_leave: {
    name: 'Annual Leave',
    icon: '🌴',
    color: '#0D9488',
    canRefuse: true
  },
  sickness: {
    name: 'Sickness',
    icon: '🤒',
    color: '#DC2626',
    canRefuse: false,
    statutory: true
  },
  sick_leave: {
    name: 'Sickness',
    icon: '🤒',
    color: '#DC2626',
    canRefuse: false,
    statutory: true
  },
  maternity: {
    name: 'Maternity Leave',
    icon: '🤰',
    color: '#7C3AED',
    canRefuse: false,
    statutory: true
  },
  paternity: {
    name: 'Paternity Leave',
    icon: '👨‍👧',
    color: '#7C3AED',
    canRefuse: false,
    statutory: true
  },
  adoption: {
    name: 'Adoption Leave',
    icon: '👨‍👩‍👧',
    color: '#7C3AED',
    canRefuse: false,
    statutory: true
  },
  shared_parental: {
    name: 'Shared Parental Leave',
    icon: '👪',
    color: '#7C3AED',
    canRefuse: false,
    statutory: true
  },
  neonatal_care: {
    name: 'Neonatal Care Leave',
    icon: '👶',
    color: '#7C3AED',
    canRefuse: false,
    statutory: true,
    isNew: true
  },
  unpaid_parental: {
    name: 'Unpaid Parental Leave',
    icon: '👨‍👩‍👧‍👦',
    color: '#7C3AED',
    canRefuse: true,
    canPostpone: true
  },
  carers_leave: {
    name: "Carer's Leave",
    icon: '🤝',
    color: '#0284C7',
    canRefuse: true,
    canPostpone: true
  },
  emergency_dependant: {
    name: 'Emergency Dependant Leave',
    icon: '🚨',
    color: '#0284C7',
    canRefuse: false,
    statutory: true
  },
  parental_bereavement: {
    name: 'Parental Bereavement',
    icon: '🕯️',
    color: '#0284C7',
    canRefuse: false,
    statutory: true,
    note: "Jack's Law"
  },
  compassionate: {
    name: 'Compassionate Leave',
    icon: '💜',
    color: '#7C3AED',
    canRefuse: true
  },
  jury_service: {
    name: 'Jury Service',
    icon: '⚖️',
    color: '#64748B',
    canRefuse: false,
    statutory: true
  },
  medical_appointment: {
    name: 'Medical Appointment',
    icon: '🏥',
    color: '#0284C7',
    canRefuse: true
  }
};

// The document each leave type normally needs, shown on the card so a manager can see at a glance whether
// it has arrived. Leave types not listed here show no documents section.
const DOC_EXPECTED = {
  maternity: 'MATB1 certificate',
  paternity: 'MATB1 and birth certificate',
  adoption: 'matching certificate',
  shared_parental: 'curtailment notice and partner declaration',
  neonatal_care: 'hospital confirmation',
  jury_service: 'jury summons',
  sickness: 'fit note (if over 7 days)',
  sick_leave: 'fit note (if over 7 days)'
};

const TimeOffApproval = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refusalReasons, setRefusalReasons] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [expandedCard, setExpandedCard] = useState(null);
  const [processing, setProcessing] = useState(null);
  
  // Refusal dialog state
  const [refusalDialog, setRefusalDialog] = useState({
    open: false,
    request: null,
    reasonCode: '',
    explanation: '',
    alternativeDates: '',
    generateLetter: true
  });

  // Approval dialog for discretionary leave
  const [approvalDialog, setApprovalDialog] = useState({
    open: false,
    request: null,
    notes: '',
    generateLetter: true
  });

  // Letter generated dialog
  const [letterDialog, setLetterDialog] = useState({
    open: false,
    filename: '',
    downloadUrl: ''
  });

  const showSnackbar = useCallback((message, severity) => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/time-off/pending');
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      showSnackbar(error.response?.data?.error || 'Error loading requests', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  const fetchRefusalReasons = useCallback(async () => {
    try {
      const response = await api.get('/time-off/refusal-reasons');
      setRefusalReasons(response.data || []);
    } catch (error) {
      console.error('Error fetching refusal reasons:', error);
      setRefusalReasons([
        { code: 'staffing_levels', name: 'Insufficient staffing cover', requires_alternative: true },
        { code: 'peak_period', name: 'Peak trading period', requires_alternative: true },
        { code: 'notice_period', name: 'Insufficient notice given', requires_alternative: true },
        { code: 'consecutive_requests', name: 'Clashes with other approved leave', requires_alternative: true },
        { code: 'training_event', name: 'Essential training or meeting', requires_alternative: true },
        { code: 'probation', name: 'During probationary period', requires_alternative: false }
      ]);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchRefusalReasons();
  }, [fetchRequests, fetchRefusalReasons]);

  const getLeaveConfig = (reasonType) => {
    return LEAVE_CONFIG[reasonType] || {
      name: reasonType?.replace(/_/g, ' ') || 'Unknown',
      icon: '📋',
      color: '#6B7280',
      canRefuse: true
    };
  };

  // Generate letter after approval/refusal
  const generateLetter = async (holidayId, type, additionalData = {}) => {
    try {
      const endpoint = type === 'approval' 
        ? `/letter-gen/generate/holiday-approval/${holidayId}`
        : `/letter-gen/generate/holiday-refusal/${holidayId}`;
      
      const response = await api.post(endpoint, {
        save_to_vault: true,
        ...additionalData
      });

      if (response.data.success) {
        setLetterDialog({
          open: true,
          filename: response.data.filename,
          downloadUrl: `/api/letter-gen/download/${response.data.filename}`
        });
      }
    } catch (error) {
      console.error('Error generating letter:', error);
      // Don't show error - letter generation is optional
    }
  };

  // Handle approval
  const handleApprove = async (request) => {
    const config = getLeaveConfig(request.reason_type);
    
    if (config.statutory) {
      await processApproval(request, 'Statutory leave acknowledged', false);
    } else {
      setApprovalDialog({
        open: true,
        request: request,
        notes: '',
        generateLetter: true
      });
    }
  };

  const processApproval = async (request, notes = '', shouldGenerateLetter = true) => {
    try {
      setProcessing(request.id);
      await api.post(`/time-off/requests/${request.id}/approve`, { notes });
      
      showSnackbar(`${getLeaveConfig(request.reason_type).name} approved for ${request.employee_name}`, 'success');
      
      // Generate letter if requested (and not statutory)
      const config = getLeaveConfig(request.reason_type);
      if (shouldGenerateLetter && !config.statutory) {
        await generateLetter(request.id, 'approval');
      }
      
      fetchRequests();
      setApprovalDialog({ open: false, request: null, notes: '', generateLetter: true });
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Error approving request', 'error');
    } finally {
      setProcessing(null);
    }
  };

  const openRefusalDialog = (request) => {
    const config = getLeaveConfig(request.reason_type);
    
    if (!config.canRefuse && !config.canPostpone) {
      showSnackbar(`${config.name} is a statutory right and cannot be refused`, 'warning');
      return;
    }
    
    setRefusalDialog({
      open: true,
      request: request,
      reasonCode: '',
      explanation: '',
      alternativeDates: '',
      generateLetter: true
    });
  };

  const handleRefuse = async () => {
    const { request, reasonCode, explanation, alternativeDates, generateLetter: shouldGenerate } = refusalDialog;
    
    if (!reasonCode) {
      showSnackbar('Please select a reason for refusal', 'warning');
      return;
    }

    try {
      setProcessing(request.id);
      await api.post(`/time-off/requests/${request.id}/refuse`, {
        refusal_reason_code: reasonCode,
        refusal_explanation: explanation,
        alternative_offered: alternativeDates
      });
      
      showSnackbar('Request refused - employee will be notified', 'info');
      
      // Generate refusal letter if requested
      if (shouldGenerate) {
        const fullReason = [
          refusalReasons.find(r => r.code === reasonCode)?.name || reasonCode,
          explanation,
          alternativeDates ? `Alternative dates offered: ${alternativeDates}` : ''
        ].filter(Boolean).join('. ');
        
        await generateLetter(request.id, 'refusal', { decline_reason: fullReason });
      }
      
      setRefusalDialog({ open: false, request: null, reasonCode: '', explanation: '', alternativeDates: '', generateLetter: true });
      fetchRequests();
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Error refusing request', 'error');
    } finally {
      setProcessing(null);
    }
  };

  const handleDownloadLetter = async () => {
  if (letterDialog.filename) {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`/api/letter-gen/download/${letterDialog.filename}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', letterDialog.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      showSnackbar('Failed to download letter', 'error');
    }
  }
  setLetterDialog({ open: false, filename: '', downloadUrl: '' });
};

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getUrgencyStyle = (urgency, daysUntil) => {
    if (urgency === 'immediate' || urgency === 'urgent') {
      return { 
        bgcolor: '#FEE2E2', 
        borderColor: '#DC2626',
        chipColor: 'error',
        label: daysUntil <= 0 ? '⚠️ Respond today' : `⚠️ ${daysUntil} days to respond`
      };
    }
    if (urgency === 'warning') {
      return { 
        bgcolor: '#FEF3C7', 
        borderColor: '#D97706',
        chipColor: 'warning',
        label: `${daysUntil} days to respond`
      };
    }
    return { 
      bgcolor: '#F0FDF4', 
      borderColor: '#16A34A',
      chipColor: 'success',
      label: `${daysUntil} days to respond`
    };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const urgentCount = requests.filter(r => r.urgency === 'urgent').length;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Time Off Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and respond to employee leave requests
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Badge badgeContent={requests.length} color="primary">
            <Chip label="Pending" variant="outlined" />
          </Badge>
          {urgentCount > 0 && (
            <Badge badgeContent={urgentCount} color="error">
              <Chip label="Urgent" color="error" variant="outlined" />
            </Badge>
          )}
        </Box>
      </Box>

      {/* Empty state */}
      {requests.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            All caught up!
          </Typography>
          <Typography color="text.secondary">
            No pending time-off requests to review
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {requests.map((request) => {
            const config = getLeaveConfig(request.reason_type);
            const urgencyStyle = getUrgencyStyle(request.urgency, request.days_until_deadline);
            const isExpanded = expandedCard === request.id;
            const isProcessing = processing === request.id;
            
            return (
              <Grid item xs={12} key={request.id}>
                <Card 
                  sx={{ 
                    border: '2px solid',
                    borderColor: urgencyStyle.borderColor,
                    bgcolor: urgencyStyle.bgcolor,
                    opacity: isProcessing ? 0.7 : 1,
                    transition: 'all 0.2s'
                  }}
                >
                  <CardContent sx={{ pb: 1 }}>
                    <Grid container spacing={2} alignItems="center">
                      {/* Left: Employee & Leave Type */}
                      <Grid item xs={12} md={4}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Typography variant="h6" component="span">
                            {config.icon}
                          </Typography>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {request.employee_name}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography 
                                variant="body2" 
                                sx={{ color: config.color, fontWeight: 500 }}
                              >
                                {config.name}
                              </Typography>
                              {config.statutory && (
                                <Tooltip title="Statutory right - cannot be refused">
                                  <Gavel sx={{ fontSize: 14, color: 'text.secondary' }} />
                                </Tooltip>
                              )}
                              {config.isNew && (
                                <Chip label="NEW" size="small" color="secondary" sx={{ height: 18, fontSize: 10 }} />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Grid>

                      {/* Middle: Dates & Duration */}
                      <Grid item xs={12} md={4}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <CalendarToday sx={{ color: 'text.secondary', fontSize: 20 }} />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {formatDate(request.start_date)}
                              {request.end_date && request.end_date !== request.start_date && (
                                <> → {formatDate(request.end_date)}</>
                              )}
                            </Typography>
                            {request.total_days && (
                              <Typography variant="caption" color="text.secondary">
                                {request.total_days} working day{request.total_days !== 1 ? 's' : ''}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>

                      {/* Right: Urgency & Actions */}
                      <Grid item xs={12} md={4}>
                        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                          <Chip 
                            icon={<AccessTime sx={{ fontSize: 16 }} />}
                            label={urgencyStyle.label}
                            color={urgencyStyle.chipColor}
                            size="small"
                            variant="outlined"
                          />
                          <IconButton 
                            size="small" 
                            onClick={() => setExpandedCard(isExpanded ? null : request.id)}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Statutory Warning Banner */}
                    {config.statutory && (
                      <Alert 
                        severity="info" 
                        icon={<Gavel />}
                        sx={{ mt: 2, py: 0.5 }}
                      >
                        <Typography variant="body2">
                          <strong>Statutory right</strong> — This leave type cannot be refused. 
                          Click "Acknowledge" to confirm receipt.
                          {config.note && <em> ({config.note})</em>}
                        </Typography>
                      </Alert>
                    )}

                    {/* Documents sent with the request */}
                    {DOC_EXPECTED[request.reason_type] && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Documents - expected: {DOC_EXPECTED[request.reason_type]}
                        </Typography>
                        <DocumentList sourceTable="time_off_requests" sourceId={request.id} canVerify />
                      </Box>
                    )}

                    {/* Expandable Details */}
                    <Collapse in={isExpanded}>
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Request submitted
                            </Typography>
                            <Typography variant="body2">
                              {formatDate(request.created_at)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Response deadline
                            </Typography>
                            <Typography variant="body2">
                              {formatDate(request.response_deadline)}
                            </Typography>
                          </Grid>
                          {request.employee_notes && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Employee notes
                              </Typography>
                              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                "{request.employee_notes}"
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Collapse>
                  </CardContent>

                  <Divider />

                  {/* Actions */}
                  <CardActions sx={{ justifyContent: 'flex-end', px: 2, py: 1.5 }}>
                    {config.statutory ? (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
                        onClick={() => handleApprove(request)}
                        disabled={isProcessing}
                      >
                        Acknowledge
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={() => openRefusalDialog(request)}
                          disabled={isProcessing}
                          sx={{ mr: 1 }}
                        >
                          Refuse
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
                          onClick={() => handleApprove(request)}
                          disabled={isProcessing}
                        >
                          Approve
                        </Button>
                      </>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Approval Dialog */}
      <Dialog 
        open={approvalDialog.open} 
        onClose={() => setApprovalDialog({ open: false, request: null, notes: '', generateLetter: true })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#DCFCE7', color: '#166534' }}>
          Approve Leave Request
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {approvalDialog.request && (
            <Box mb={2}>
              <Typography variant="body1">
                Approve <strong>{getLeaveConfig(approvalDialog.request.reason_type).name}</strong> for{' '}
                <strong>{approvalDialog.request.employee_name}</strong>?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {formatDate(approvalDialog.request.start_date)} → {formatDate(approvalDialog.request.end_date)}
                {approvalDialog.request.total_days && ` (${approvalDialog.request.total_days} days)`}
              </Typography>
            </Box>
          )}
          
          <TextField
            fullWidth
            label="Notes (optional)"
            multiline
            rows={2}
            value={approvalDialog.notes}
            onChange={(e) => setApprovalDialog(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Any notes for the employee..."
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={approvalDialog.generateLetter}
                onChange={(e) => setApprovalDialog(prev => ({ ...prev, generateLetter: e.target.checked }))}
                color="primary"
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Description fontSize="small" />
                <span>Generate approval letter</span>
              </Box>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog({ open: false, request: null, notes: '', generateLetter: true })}>
            Cancel
          </Button>
          <Button 
            onClick={() => processApproval(approvalDialog.request, approvalDialog.notes, approvalDialog.generateLetter)}
            color="success" 
            variant="contained"
            disabled={processing}
          >
            Confirm Approval
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refusal Dialog */}
      <Dialog 
        open={refusalDialog.open} 
        onClose={() => setRefusalDialog({ open: false, request: null, reasonCode: '', explanation: '', alternativeDates: '', generateLetter: true })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
          Refuse Leave Request
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You <strong>must</strong> provide a business reason for refusing this request. 
            This will be recorded in the audit trail.
          </Alert>

          {refusalDialog.request && (
            <Box mb={2} p={2} bgcolor="grey.50" borderRadius={1}>
              <Typography variant="body2">
                <strong>{refusalDialog.request.employee_name}</strong> — {' '}
                {getLeaveConfig(refusalDialog.request.reason_type).name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(refusalDialog.request.start_date)} → {formatDate(refusalDialog.request.end_date)}
              </Typography>
            </Box>
          )}
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Reason for Refusal *</InputLabel>
            <Select
              value={refusalDialog.reasonCode}
              onChange={(e) => setRefusalDialog(prev => ({ ...prev, reasonCode: e.target.value }))}
              label="Reason for Refusal *"
            >
              {refusalReasons.map(reason => (
                <MenuItem key={reason.code} value={reason.code}>
                  {reason.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Additional Explanation"
            multiline
            rows={2}
            value={refusalDialog.explanation}
            onChange={(e) => setRefusalDialog(prev => ({ ...prev, explanation: e.target.value }))}
            sx={{ mb: 2 }}
            placeholder="Provide context for the employee..."
          />

          <TextField
            fullWidth
            label="Alternative Dates Offered"
            value={refusalDialog.alternativeDates}
            onChange={(e) => setRefusalDialog(prev => ({ ...prev, alternativeDates: e.target.value }))}
            placeholder="e.g., Week of 15th January would work better"
            helperText="Offering alternatives demonstrates good faith and reduces tribunal risk"
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={refusalDialog.generateLetter}
                onChange={(e) => setRefusalDialog(prev => ({ ...prev, generateLetter: e.target.checked }))}
                color="primary"
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Description fontSize="small" />
                <span>Generate refusal letter</span>
              </Box>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefusalDialog({ open: false, request: null, reasonCode: '', explanation: '', alternativeDates: '', generateLetter: true })}>
            Cancel
          </Button>
          <Button 
            onClick={handleRefuse} 
            color="error" 
            variant="contained"
            disabled={!refusalDialog.reasonCode || processing}
          >
            Confirm Refusal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Letter Generated Dialog */}
      <Dialog
        open={letterDialog.open}
        onClose={() => setLetterDialog({ open: false, filename: '', downloadUrl: '' })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Description color="primary" />
          Letter Generated
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Your letter has been generated and saved to the document vault.
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="medium">
              {letterDialog.filename}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLetterDialog({ open: false, filename: '', downloadUrl: '' })}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownloadLetter}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TimeOffApproval;